import React from "react";
import { debounce } from "@/utils/debounce";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type Row,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type FilterFn,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  Search,
  Sliders,
  Zap,
  Edit2,
  LoaderCircle,
  SquarePen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { Category, ProductVariant, QuickReorder } from "@/types/AppTypes";
import { DropdownMenuItem } from "@radix-ui/react-dropdown-menu";
import { useAppSelector } from "@/store/hooks";
import { parseJSONField } from "@/utils/helper/parseJSON";
import { ScrollArea } from "../ui/scroll-area";
import capitalizeWords from "@/utils/helper/capitalizeWords";

type UpdateBuyerProductStockTableProps = {
  products: QuickReorder[];
  categories: Category[];
  handleStockUpdate?: (product: QuickReorder, stock: number) => void;
  stockLoading?: boolean;
  handleReorderProduct?: (product: QuickReorder, quantity: number) => void;
  dialogOpen: boolean;
  setDialogOpen: (arg0: boolean) => void;
  reorderLoading?: boolean;
  children?: React.ReactNode;
};

declare module "@tanstack/react-table" {
  interface FilterFns {
    categoryFilter: FilterFn<any>;
  }
}

const UpdateBuyerProductStockTable: React.FC<UpdateBuyerProductStockTableProps> = React.memo(
  ({ products: productsProp, categories, handleStockUpdate, stockLoading, children }) => {
    const [products, setProducts] = React.useState<QuickReorder[]>(productsProp);
    const { categoriesMap } = useAppSelector((state) => state.app);

    // Stock edit dialog state
    const [editDialogOpen, setEditDialogOpen] = React.useState(false);
    const [editingProduct, setEditingProduct] = React.useState<QuickReorder | null>(null);
    const [stockInput, setStockInput] = React.useState<string>("");

    React.useEffect(() => {
      setProducts(productsProp);
    }, [productsProp]);

    const categoriesWithAll = React.useMemo(
      () => [{ name: "all", label: "All Categories" }, ...(categories || [])],
      [categories]
    );

    const [inputValue, setInputValue] = React.useState("");
    const [searchQuery, setSearchQuery] = React.useState("");
    const deferredSearchQuery = React.useDeferredValue(searchQuery);
    const [rowSelection, setRowSelection] = React.useState({});
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [pagination, setPagination] = React.useState({ pageIndex: 0, pageSize: 10 });

    const [selectedCategories, setSelectedCategories] = React.useState<Record<string, boolean>>({
      all: true,
    });

    const handleCategoryChange = (categoryName: string) => {
      setSelectedCategories((prev) => {
        const newSelection = { ...prev };
        newSelection[categoryName] = !prev[categoryName];
        if (categoryName === "all" && newSelection["all"]) {
          Object.keys(newSelection).forEach((key) => {
            if (key !== "all") newSelection[key] = false;
          });
        }
        if (categoryName !== "all" && newSelection[categoryName]) {
          newSelection["all"] = false;
        }
        return newSelection;
      });
    };

    React.useEffect(() => {
      const activeCategories = Object.keys(selectedCategories).filter(
        (cat) => selectedCategories[cat] && cat !== "all"
      );
      if (activeCategories.length > 0) {
        setColumnFilters((prev) => {
          const otherFilters = prev.filter((filter) => filter.id !== "category");
          return [
            ...otherFilters,
            {
              id: "category",
              value: activeCategories,
              filterFn: "categoryFilter",
            },
          ];
        });
      } else {
        setColumnFilters((prev) => prev.filter((filter) => filter.id !== "category"));
      }
    }, [selectedCategories]);

    // Handle opening edit dialog
    const handleEditStock = (product: QuickReorder) => {
      const variant = parseJSONField(product?.variant);
      setEditingProduct({ ...product, variant });
      setStockInput(product?.stock?.toString() || "0");
      setEditDialogOpen(true);
    };

    // Handle closing edit dialog
    const handleCloseEditDialog = () => {
      setEditDialogOpen(false);
      setEditingProduct(null);
      setStockInput("");
    };

    // Handle confirming stock update
    const handleConfirmStockUpdate = async () => {
      if (editingProduct && handleStockUpdate) {
        const newStock = parseInt(stockInput, 10);
        if (!isNaN(newStock) && newStock >= 0 ) {
          await handleStockUpdate(editingProduct, newStock);
          if(!stockLoading) {
            handleCloseEditDialog();
          }
        }
      }
    };

    const columns: ColumnDef<any>[] = [
      {
        accessorKey: "image",
        header: () => <div className="pl-3">Image</div>,
        cell: ({ row }) => {
          const variant = row.original?.variant
            ? (JSON.parse(row.original?.variant) as ProductVariant)
            : null;

          return (
            <div className="relative w-16 h-12 overflow-hidden rounded-md ml-2">
              <img
                src={variant?.images?.[0] || "/placeholder.webp"}
                alt={variant?.name}
                className="absolute inset-0 w-full h-full object-fill"
              />
            </div>
          );
        },
      },
      {
        id: "product",
        accessorKey: "name",
        header: "Product",
        cell: ({ row }) => {
          const variant = row.original?.variant
            ? (JSON.parse(row.original?.variant) as ProductVariant)
            : null;
          return (
            <div>
              <div className="flex items-center gap-1">
                <span className="font-medium">{capitalizeWords(variant?.name || "")}</span>
              </div>
              {row.original?.quantity && (
                <span className="text-sm text-muted-foreground">{row.original?.quantity}</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "stock",
        header: () => <div className="text-center">Stock</div>,
        cell: ({ row }) => {
          const product = row.original;
          return (
            <div className="flex items-center justify-center gap-2 px-4 text-center w-full ">
              <div className="pl-2 flex items-center justify-center">
                <div className="text-center text-black">{product.stock || "0"}</div>
                <Button
                  className="ml-1 text-gray-500 hover:text-black bg-transparent"
                  onClick={() => handleEditStock(product)}
                  title="Edit Stock"
                  size="sm"
                  variant="ghost"
                >
                  <SquarePen size={16} />
                </Button>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "category",
        header: "Category",
        filterFn: "categoryFilter",
        cell: ({ row }) => {
          const categories = row.original?.category_id;
          if (Array.isArray(categories) && categories.length > 0) {
            return (
              <>
                {categories
                  .map((catId: string) => categoriesMap?.[catId]?.name)
                  .filter(Boolean)
                  .join(", ") || "N/A"}
              </>
            );
          }
          return <>N/A</>;
        },
      },
    ];

    React.useEffect(() => {
      if (deferredSearchQuery) {
        setColumnFilters((prev) => {
          const categoryFilter = prev.find((filter) => filter.id === "category");
          const filters = categoryFilter ? [categoryFilter] : [];
          return [
            ...filters,
            {
              id: "product",
              value: deferredSearchQuery,
            },
          ];
        });
      } else {
        setColumnFilters((prev) => prev.filter((filter) => filter.id !== "product"));
      }
    }, [deferredSearchQuery]);

    const sortableId = React.useId();
    const sensors = useSensors(
      useSensor(MouseSensor, {}),
      useSensor(TouchSensor, {}),
      useSensor(KeyboardSensor, {})
    );

    const dataIds = React.useMemo<UniqueIdentifier[]>(
      () => products?.map(({ id }) => id) || [],
      [products]
    );

    const table = useReactTable({
      data: products,
      columns,
      state: {
        sorting,
        columnVisibility,
        rowSelection,
        columnFilters,
        pagination,
      },
   filterFns: {
    categoryFilter: (row, columnId, filterValues) => {
      if (!filterValues.length) return true;
      
      const categoryIds = row.original?.category_id;
      if (Array.isArray(categoryIds) && categoryIds.length > 0) {
        const categoryNames = categoryIds
          .map((catId: string) => categoriesMap?.[catId]?.name)
          .filter((name): name is string => Boolean(name)); 
        
        return categoryNames.some((categoryName: string) => 
          filterValues.includes(categoryName)
        );
      }
      return false;
    },
  },
      getRowId: (row) => row.id,
      enableRowSelection: true,
      onRowSelectionChange: setRowSelection,
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      onColumnVisibilityChange: setColumnVisibility,
      onPaginationChange: setPagination,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFacetedRowModel: getFacetedRowModel(),
      getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    function handleDragEnd(event: DragEndEvent) {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        setProducts((products) => {
          const oldIndex = dataIds.indexOf(active.id);
          const newIndex = dataIds.indexOf(over.id);
          return arrayMove(products, oldIndex, newIndex);
        });
      }
    }

    function DraggableRow({ row }: { row: Row<ProductVariant> }) {
      const { transform, transition, setNodeRef, isDragging } = useSortable({
        id: row.original.id,
      });
      return (
        <TableRow
          data-state={row.getIsSelected() && "selected"}
          data-dragging={isDragging}
          ref={setNodeRef}
          className="relative z-0 data-[dragging=true]:z-10 data-[dragging=true]:opacity-80"
          style={{
            transform: CSS.Transform.toString(transform),
            transition: transition,
          }}
        >
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
      );
    }

    const debouncedSetSearch = React.useMemo(
      () =>
        debounce((value: string) => {
          setSearchQuery(value);
        }, 200),
      []
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      debouncedSetSearch(value);
    };

    return (
      <div className="container mx-auto py-12 px-4 max-w-7xl">
        <div className="flex items-center gap-2 mb-6">
          <Zap className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Update Product Stock</h1>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              className="pl-10 focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus:border-none bg-white"
              value={inputValue}
              onChange={handleInputChange}
            />
          </div>
          <div className="flex gap-2 md:flex-row flex-col">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  <Sliders className="h-4 w-4" />
                  Filter
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white py-4 pr-16 pl-2">
                <DropdownMenuItem className="text-md hover:border-none hover:outline-none pl-2 font-medium pb-2">
                  Filter by Category
                </DropdownMenuItem>

                <DropdownMenuCheckboxItem
                  className="hover:border-none hover:outline-none focus:bg-white hover:bg-white cursor-pointer"
                  checked={selectedCategories["all"] || false}
                  onCheckedChange={() => handleCategoryChange("all")}
                >
                  All Categories
                </DropdownMenuCheckboxItem>
<ScrollArea className="max-h-52">
               

                {categoriesWithAll
                  .filter((category) => category.name !== "all")
                  .map((category: Category) => (
                    <DropdownMenuCheckboxItem
                      className="hover:border-none hover:outline-none focus:bg-white hover:bg-white cursor-pointer "
                      key={category.name}
                      checked={selectedCategories[category?.name ?? ""]}
                      onCheckedChange={() => handleCategoryChange(category?.name ?? "")}
                    >
                      {category?.name}
                    </DropdownMenuCheckboxItem>
                  ))}
                     
                </ScrollArea>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              className="whitespace-nowrap bg-black hover:bg-gray-800 text-white"
              disabled={Object.keys(rowSelection).length === 0}
            >
              Add Selected to Cart<span className="w-5">({Object.keys(rowSelection).length})</span>
            </Button>
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
            sensors={sensors}
            id={sortableId}
          >
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-muted">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
                    {table.getRowModel().rows.map((row) => (
                      <DraggableRow key={row.id} row={row} />
                    ))}
                  </SortableContext>
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </DndContext>
        </div>

        <div className="flex items-center justify-between px-4 mt-4">
          <div className="hidden flex-1 text-sm text-muted-foreground lg:flex">
            {table.getFilteredSelectedRowModel().rows.length} of{" "}
            {table.getFilteredRowModel().rows.length} row(s) selected.
          </div>
          <div className="flex w-full items-center gap-8 lg:w-fit">
            <div className="hidden items-center gap-2 lg:flex">
              <Label htmlFor="rows-per-page" className="text-sm font-medium">
                Rows per page
              </Label>
              <Select
                value={`${table.getState().pagination.pageSize}`}
                onValueChange={(value) => {
                  table.setPageSize(Number(value));
                }}
              >
                <SelectTrigger className="w-20" id="rows-per-page">
                  <SelectValue placeholder={table.getState().pagination.pageSize} />
                </SelectTrigger>
                <SelectContent side="top" className="bg-white">
                  {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                    <SelectItem key={pageSize} value={`${pageSize}`}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="ml-auto flex items-center gap-2 lg:ml-0">
             {children}
            </div>
          </div>
        </div>

        {/* Stock Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Update Stock</DialogTitle>

            
                <div className="flex flex-row items-center gap-4 my-1">
                  <div className="relative w-20 h-16 overflow-hidden rounded-md">
                    <img
                      src={editingProduct?.variant?.images?.[0] || "/placeholder.webp"}
                      alt="Product"
                      className="absolute inset-0 w-full h-full object-fill"
                    />
                  </div>
                    <DialogDescription>
                      {editingProduct?.variant?.name || "Product"}
                    </DialogDescription>
                </div>
           
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="stock" className="text-right">
                  Stock
                </Label>
                <Input
                  id="stock"
                  type="number"
                  min="0"
                  className="col-span-3"
                  value={stockInput}
                  onChange={(e) => setStockInput(e.target.value)}
                  placeholder="Enter stock quantity"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseEditDialog}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirmStockUpdate}
                disabled={
                  stockInput === "" ||
                  isNaN(parseInt(stockInput, 10)) ||
                  parseInt(stockInput, 10) < 0
                }
              >
                {
                    stockLoading ? (
                      <div className="flex items-center gap-2">
                        <LoaderCircle className="w-4 h-4 mr-1 animate-spin" />
                        Updating...
                      </div>
                    ) : (
                      <>
                      Update Stock
                      </>
                    )
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
);

export default UpdateBuyerProductStockTable;
